import { isOriginInUse, resolveDefaultSortKey } from '../../util/search';
import { parse, stringify } from '../../util/urlHelpers';
import { isArrayLength } from '../../util/genericHelpers';
import { SearchType } from '../../util/enums';
import { PUBLIC_MOORAGES_QUERY_PARAM } from '../../util/publicMoorages';
import {
  ACCESS_FILTER_ALL,
  ACCESS_FILTER_QUERY_PARAM,
} from '../../util/publicMoorageConstants';
import { validFilterParams } from './SearchPage.shared';

const parseSearchParams = search =>
  parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

export const buildCanonicalMapSearchParams = ({
  locationSearch,
  config,
  filterConfigs,
  viewportBounds,
  viewportCenter,
  resetPage = true,
}) => {
  const parsed = parseSearchParams(locationSearch);
  const { address, page, origin: currentOrigin, ...otherParams } = parsed;

  const filterParams = validFilterParams(otherParams, filterConfigs, false);
  const sortConfig = config?.search?.sortConfig;
  const sortParamName = sortConfig?.queryParamName || 'sort';
  const hasSortParam = Boolean(filterParams?.[sortParamName]);
  const nextOrigin = isOriginInUse(config) ? viewportCenter || currentOrigin : null;

  const baseParams = {
    ...(address ? { address } : {}),
    ...(nextOrigin ? { origin: nextOrigin } : {}),
    ...filterParams,
    bounds: viewportBounds,
    mapSearch: true,
    ...(resetPage ? { page: 1 } : page ? { page } : {}),
  };

  const defaultSort = resolveDefaultSortKey(sortConfig, baseParams);
  const sortMaybe = !hasSortParam && defaultSort ? { [sortParamName]: defaultSort } : {};

  return {
    ...baseParams,
    ...sortMaybe,
  };
};

export const buildPublicMooragesToggleSearchParams = ({
  locationSearch,
  filterConfigs,
  enabled,
  access,
}) => {
  const currentParams = { ...parseSearchParams(locationSearch) };
  delete currentParams.page;
  const validParams = validFilterParams(currentParams, filterConfigs, false);

  if (access && access !== ACCESS_FILTER_ALL) {
    const paramsWithoutLegacyToggle = { ...validParams };
    delete paramsWithoutLegacyToggle[PUBLIC_MOORAGES_QUERY_PARAM];
    return {
      ...paramsWithoutLegacyToggle,
      [ACCESS_FILTER_QUERY_PARAM]: access,
    };
  }

  const paramsWithoutPublicMoorages = { ...validParams };
  delete paramsWithoutPublicMoorages[PUBLIC_MOORAGES_QUERY_PARAM];
  delete paramsWithoutPublicMoorages[ACCESS_FILTER_QUERY_PARAM];
  return paramsWithoutPublicMoorages;
};

export const getSynchronizedListings = ({ listings, listingsAreLoaded }) => {
  return listingsAreLoaded ? listings : [];
};

export const hasSameSearchQueryParams = (currentSearchParams, nextSearchParams) => {
  return stringify(currentSearchParams || {}) === stringify(nextSearchParams || {});
};

export const shouldFetchExtendedBoundsAfterSearch = ({
  listings,
  previousSearchInProgress,
  searchInProgress,
  hasExtendedBoundsFetched,
  hasMarketingReminder,
  searchType,
  isMapSearchActive,
  suppressAutoExpand,
}) => {
  return (
    !searchInProgress &&
    !hasMarketingReminder &&
    searchType === SearchType.LOCATION &&
    previousSearchInProgress &&
    !hasExtendedBoundsFetched &&
    !isMapSearchActive &&
    !suppressAutoExpand &&
    !isArrayLength(listings)
  );
};
